import { AppBar, Tab, Tabs } from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import { Redirect, Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import Panel from "../../../../components/Panel";
import eventEmitter from "../../../../components/SafeDeskEventProvider";
import CommentTaskType from "../../../../enum/CommentTaskType";
import TaskSendEmailType from "../../../../enum/TaskSendEmailType";
import AttachmentTask from "../../../../model/AttachmentTask";
import FinalCustomer from "../../../../model/FinalCustomer";
import Software from "../../../../model/Software";
import Task from "../../../../model/Task";
import TaskReminder from "../../../../model/TaskReminder";
import User from "../../../../model/User";
import { useAuthentication } from "../../../../reducer/authentication";
import { useLoading } from "../../../../reducer/loading";
import ProjectApi from "../../../../service/ProjectApi";
import { ServiceApi } from "../../../../service/ServiceApiWithLoading";
import SoftwareApi from "../../../../service/SoftwareApi";
import TaskApi from "../../../../service/TaskApi";
import TechnicalAssistanceApi from "../../../../service/TechnicalAssistanceApi";
import { delay, fnsSetTime, getUserDepartmentPermissions } from "../../../../utils";
import { TicketLayoutTypeRule } from "../../Config/TicketLayoutTypes/TicketLayoutTypeForm";
import RouteIdParams from "../../RouteIdParams";
import TaskForm, { FirmwareFormDialogData, setLastUsedProjects, setLastUsedTags, TaskChecklistRow, TaskFormData } from "./TaskForm";
import TaskOpenShake from "./TaskOpenShake";
import TaskPostServiceForm, { TaskPostServiceFormData } from "./TaskPostServiceForm";
import TaskRecurrenceForm, { TaskRecurrenceFormData } from "./TaskRecurrenceForm";
import TicketTypeApprovalEmailLayout from "../../../../enum/TicketTypeApprovalEmailLayout";

const api = new TaskApi();
const taskReminderApi = new ServiceApi<TaskReminder>('taskReminder');
const projectApi = new ProjectApi();
const softwareApi = new SoftwareApi();
const technicalAssistanceApi = new TechnicalAssistanceApi();
const finalCustomerApi = new ServiceApi<FinalCustomer>('finalCustomer');
const userApi = new ServiceApi<User>('user');

type TaskEditTabValue = 'general' | 'post-service' | 'recurrence';
type TaskEditTabLabel = 'Geral' | 'Pós-Atendimento' | 'Recorrência';
type TaskEditTab = { label: TaskEditTabLabel, value: TaskEditTabValue };

interface TaskEditTabsProps {
    value?: TaskEditTab | string;
    onTabChange(tab: TaskEditTab): void;
    editing: boolean;
    isCommbox: boolean;
    user?: User;
    item: Task;
}

function TaskEditTabs(props: TaskEditTabsProps) {
    const { value, editing, onTabChange, isCommbox, user, item } = props;
    const permissionByDepartment = getUserDepartmentPermissions(user);
    const tabs: TaskEditTab[] = [
        { label: 'Geral', value: 'general' }
    ];
    if (permissionByDepartment.perm_menu_post_service) {
        if (item.ticket_type?.ticket_layout_type?.rule_post_service_tab !== TicketLayoutTypeRule.NotVisible) {
            tabs.push({ label: 'Pós-Atendimento', value: 'post-service' });
        }
        if (isCommbox === true) {
            tabs.push({ label: 'Recorrência', value: 'recurrence' });
        }
    }

    return (
        <Tabs
            value={value}
            onChange={(ev, v) => onTabChange(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
        >
            {tabs.map((ob) => {
                const disabled = editing && ob.value !== value;
                return <Tab
                    key={ob.value}
                    value={ob.value}
                    disabled={disabled}
                    //title={disabled ? t('saveOrUndoBeforeSwitchingTabs', { capitalize: true }) : ''}
                    label={ob.label}
                />
            })}
        </Tabs>
    );
}

interface TaskEditProps {
    projectId?: number;
}

export const isSameDay = (dateString: string | null | undefined) => {
    if (!dateString) {
        return false;
    }
    const date = moment(dateString);
    return date.isValid() && date.isSame(moment(), "day");
};

const apiFinalCustomer = new ServiceApi<FinalCustomer>('finalCustomer');

export default function TaskEdit(props: TaskEditProps) {
    const { projectId } = props;
    const match = useRouteMatch<RouteIdParams>();
    const id = match.params.id;
    const { user } = useAuthentication();
    const setLoading = useLoading();

    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [tabKey, setTabKey] = useState<number>(0);
    const [editing, setEditing] = useState<boolean>(false);
    const [item, setItem] = useState<Task>();
    const [commboxId, setCommboxId] = useState<number | null>(null);
    const permissionByDepartment = getUserDepartmentPermissions(user);

    function loadItem() {
        return fetchItem().then((item) => {
            apiFinalCustomer.getList({ where: [['name', '=', 'Commbox']] }).then((finalCustomerList) => {
                if (finalCustomerList.length > 0) {
                    setCommboxId(finalCustomerList[0].id);
                }
            });
        });
    }

    useEffect(() => {
        loadItem();
        eventEmitter.on('taskSaved', onReceiveTaskSaved);
        return () => {
            eventEmitter.off('taskSaved', onReceiveTaskSaved);
        }
    }, [id]);

    function onReceiveTaskSaved(o: any) {
        //console.log('ihaa', o?.submitType, o);
        if (!o?.submitType) {
            fetchItem().then((a) => {
                onUndo();
            });
        }
    }

    function fetchItem() {
        return api.getById(id, {
            relations: ['products', 'comments', 'comments.user', 'comments.user.current_or_next_vacation', 'comments.time_counter', 'comments.comment_task_users', 'comments.comment_task_users.user', 'comments.notify_others_users',
                'comments.comment_task_users.user.current_or_next_vacation', 'comments.attachments', 'comments.attachments.system_attachment', 'attachments', 'attachments.system_attachment', 'created_by_user', 'customer_contacts', 'final_customer', 'observers', 'tags',
                'checklists', 'user', 'user.current_or_next_vacation', 'created_by_user.current_or_next_vacation', 'technical_assistances', 'technical_assistances.ticket_status', 'technical_assistances.integrator',
                'technical_assistances.final_customer', 'technical_assistances.product', 'ticket_type.ticket_layout_type', 'user.department', 'user.departments', 'created_by_user.department', 'created_by_user.departments',
                'references', 'references.ticket_status', 'references.user', 'referenced_by', 'referenced_by.ticket_status', 'referenced_by.user', 'comments.user.department', 'comments.user.departments',
                'comments.comment_task_users.user.department', 'comments.comment_task_users.user.departments', 'comments.emotes', 'comments.emotes.user', 'comments.generated_task', 'comments.generated_task.user',
                'comments.generated_task.ticket_status'
            ]
        }).then(setItem);
    }

    function onTabChange(tab: TaskEditTab) {
        loadItem().then(() => {
            history.replace(`${match.url}/${tab}`);
        });
    }

    async function onSubmit(values: TaskFormData, attachmentsRemoved: AttachmentTask[], isDirty?: boolean,
        sendEmailStatusClosed?: boolean, submitType?: string | null, oldUserId?: number | null, isInternal?: boolean, isStatusChanged?: boolean,
        oldTicketStatusId?: number | null, taskReminders?: Partial<TaskReminder>[], oldProjectId?: number | null, checklistsRemoved?: TaskChecklistRow[],
        newFirmwares?: FirmwareFormDialogData[], newFirmwaresLinks?: number[]) {
        setLoading(true);
        try {
            if (!values.final_customer_id) return;
            const finalCustomer = await finalCustomerApi.getById(values.final_customer_id);
            const result = await api.update(id, {
                name: values.name,
                description: values.description,
                solution: values.solution,
                ticket_status_id: values.ticket_status_id,
                project_id: values.project_id,
                final_customer_id: values.final_customer_id,
                ticket_type_id: values.ticket_type_id,
                ticket_cause_id: values.ticket_cause_id,
                ticket_root_cause_id: values.ticket_root_cause_id,
                user_id: values.user_id,
                ticket_origin_id: values.ticket_origin_id,
                ticket_solution_type_id: values.ticket_solution_type_id,
                priority: values.priority,
                effort_estimation: values.effort_estimation,
                required_dev_attach: values.required_dev_attach,
                delivery_forecast: values.delivery_forecast ? fnsSetTime(values.delivery_forecast, 0, 0, 0).toISOString() : null,
                scheduled_date: values.scheduled_date ? moment(values.scheduled_date).format('YYYY-MM-DD HH:mm:00') : null,
                prod_amount_to_be_produced: values.prod_amount_to_be_produced || 0,
                type_has_changed: values.type_has_changed,
                feedback_to_improve_item: values.feedback_to_improve_item,
                feedback_to_improve_text: values.feedback_to_improve_text,
                requires_email_approval: item?.requires_email_approval || values.comments.some(c => c.send_comment_to_approver) || false
            });
            if (!result) throw new Error('Erro ao salvar chamado');
            if (oldProjectId && values.project_id && oldProjectId != values.project_id) {
                await projectApi.updateProjectTimeChange(id, oldProjectId, values.project_id);
            }

            if (values.project_id) {
                const project = await projectApi.getById(values.project_id);
                setLastUsedProjects(project.id, project.name);
            }

            await api.updateProducts(id, [...values.products_h, ...values.products_s]);
            await api.updateCustomerContacts(id, values.customer_contacts);
            await api.updateObservers(id, values.observers);
            await api.updateTags(id, values.tags.map((ta) => {
                if (item?.tags?.findIndex((o) => o.id === parseInt(ta.id)) === -1) {
                    setLastUsedTags(ta);
                }
                return ta.id
            }));
            await api.updateReferences(id, values.references.map((ref) => ref.id));
            const insertedFirmwares: Software[] = [];
            if (newFirmwares && newFirmwares.length > 0) {
                await Promise.all(newFirmwares.map(async (firmware, index) => {
                    const resultFirmware = await softwareApi.insert({
                        name: firmware.name,
                        firmware_date: fnsSetTime(new Date(), 0, 0, 0).toISOString(),
                        homologated_at: null,
                        general_observations: firmware.general_observations,
                        known_bugs: firmware.known_bugs,
                        new_features: firmware.new_features,
                        version: firmware.version,
                        type: firmware.type,
                        version_status: firmware.version_status,
                        corrections_made: firmware.corrections_made
                    });
                    if (resultFirmware) {
                        insertedFirmwares.push(resultFirmware);
                        if (firmware.data) {
                            await softwareApi.uploadFile(firmware.data, resultFirmware.id, 1);
                        }
                        if (firmware.data_2) {
                            await softwareApi.uploadFile(firmware.data_2, resultFirmware.id, 2);
                        }
                        if (firmware.data_3) {
                            await softwareApi.uploadFile(firmware.data_3, resultFirmware.id, 3);
                        }
                        if (firmware.data_4) {
                            await softwareApi.uploadFile(firmware.data_4, resultFirmware.id, 4);
                        }
                        if (firmware.data_5) {
                            await softwareApi.uploadFile(firmware.data_5, resultFirmware.id, 5);
                        }
                        await softwareApi.updateLinks(resultFirmware.id, [{ name: `Chamado #${item?.ticket_code}`, software_version_id: resultFirmware.id, url: `http://safedesk.commbox.com.br/#/system/task/${id}/general` }]);
                    }
                }))
            }
            if (newFirmwaresLinks && newFirmwaresLinks.length > 0) {
                await Promise.all(newFirmwaresLinks.map(async (firmwareId, index) => {
                    const resultFirmware = await softwareApi.getById(firmwareId, { relations: ['software_version_links'] });
                    if (resultFirmware) {
                        let links = resultFirmware.software_version_links || [];
                        links.push({ name: `Chamado #${item?.ticket_code}`, software_version_id: firmwareId, url: `http://safedesk.commbox.com.br/#/system/task/${item?.id}/general` });
                        await softwareApi.updateLinks(firmwareId, links);
                    }
                }
                ))
            }
            const relationCommentIdxId: { idx: number, id: number }[] = [];
            if (values.comments && values.comments.length > 0) {
                await Promise.all(values.comments.map(async (comment, index) => {
                    if (comment.id === 0 && comment.comment) {
                        let commentValue = comment.comment;
                        insertedFirmwares.forEach((firmware) => {
                            if (commentValue.includes(`{[LINK_FIRMWARE_${firmware.name}_${firmware.version}]}`)) {
                                commentValue = commentValue.replace(`{[LINK_FIRMWARE_${firmware.name}_${firmware.version}]}`, `<a href="http://safedesk.commbox.com.br/#/system/administration/software/${firmware.id}/general" target="_blank">http://safedesk.commbox.com.br/#/system/administration/software/${firmware.id}/general</a>`);
                            }
                        });
                        let notify_others_users = comment.notify_others_users?.map((u) => u.id) || [];
                        let savedComment = await api.saveComment(id, commentValue, comment.is_public, comment.user_id, comment.type, comment.time_counter?.hours,
                            comment.time_counter?.minutes, comment.comment_task_users?.map((o) => o.user_id), comment.prod_amount, comment.prod_amount_problem, comment.send_comment_to_approver, notify_others_users);
                        relationCommentIdxId.push({ idx: index, id: savedComment.id });
                    }
                    return;
                }))
            }
            if (checklistsRemoved && checklistsRemoved.length > 0) {
                for (const checklistRemoved of checklistsRemoved) {
                    await api.deleteChecklist(checklistRemoved.id);
                }
            }
            if (values.checklists && values.checklists.length > 0) {
                for (const checklist of values.checklists) {
                    if (checklist.id === 0) {
                        await api.saveChecklist(id, checklist.checklist_id, checklist.is_mandatory);
                    }
                }
            }
            if (attachmentsRemoved && attachmentsRemoved.length > 0) {
                for (const attachmentRemoved of attachmentsRemoved) {
                    await api.deleteAttachment(attachmentRemoved.id);
                }
            }
            if (values.attachments && values.attachments.length > 0) {
                for (const attachment of values.attachments) {
                    if (attachment.id > 0) {
                        if(attachment.edited === true){
                            await api.updateAttachment(attachment.id, attachment.description, attachment.type);
                        }                        
                    } else if (attachment.file || attachment.system_attachment_id) {
                        let commentRelItem = relationCommentIdxId.find(o => o.idx === attachment.commentIdx)
                        await api.saveAttachment(attachment.file || null, id, attachment.description, attachment.type, commentRelItem?.id, attachment.system_attachment_id);
                    }
                }
            }
            if (values.technical_assistances && values.technical_assistances.length > 0) {
                const newTechAssisIds: number[] = [];
                await Promise.all(values.technical_assistances.map(async (ta) => {
                    if (!ta.id) {
                        let techAssisResult = await technicalAssistanceApi.insert({ ...ta, task_id: parseInt(id), final_customer_id: values.final_customer_id, integrator_id: finalCustomer.integrator_id });
                        if (techAssisResult) {
                            newTechAssisIds.push(techAssisResult.id);
                            await technicalAssistanceApi.updateCustomerContacts(techAssisResult.id, values.customer_contacts);
                        }
                    }
                }))
            }
            if (sendEmailStatusClosed && user) {
                await api.sendEmail(id, TaskSendEmailType.StatusChange, user.id);
            } else if (isStatusChanged === true && user) {
                await api.sendEmail(id, TaskSendEmailType.StatusChangeObserver, user.id, oldTicketStatusId);
            }
            if (oldUserId) {
                await api.sendEmail(id, TaskSendEmailType.AssignedToChange, oldUserId);
            }
            if (values.solution_time && values.solution_time !== '00:00' && user) {
                var [hours, minutes] = values.solution_time.split(':');
                if (hours || minutes) {
                    let hoursInt = hours ? parseInt(hours) : 0;
                    let minutesInt = minutes ? parseInt(minutes) : 0;
                    let savedComment = await api.saveComment(id, 'Horas automaticamente inseridas na Resolução da tarefa', false, user.id, CommentTaskType.PreService, hoursInt, minutesInt);
                    relationCommentIdxId.push({ idx: 999, id: savedComment.id });
                }
            }
            if (taskReminders && taskReminders.length > 0) {
                await Promise.all(taskReminders.map(async (taskReminder, index) => {
                    return await taskReminderApi.insert({ task_id: parseInt(id), user_id: taskReminder.user_id, comment: taskReminder.comment, reminder_date: taskReminder.reminder_date, user_ids: taskReminder.user_ids });
                }))
            }
            if (relationCommentIdxId.length > 0) {
                await api.sendCommentsEmail(relationCommentIdxId.map(o => o.id));
            }
            const emitTaskSaved: any = { ...item, ...values, submitType };
            eventEmitter.emit('taskSaved', emitTaskSaved);
            enqueueSnackbar('Chamado salvo com sucesso!', { variant: 'success' });
            if (user?.id && !!user?.use_lanza_coin) {
                try {
                    const refreshedUser = await userApi.getById(user.id);
                    const recordedMinutes = Number(refreshedUser?.time_recorded_today ?? 0);
                    const hasRecordedToday = isSameDay(refreshedUser?.last_time_recorded_today_at);
                    const alertedToday = isSameDay(refreshedUser?.last_alert_eight_hours_recorded_at);

                    if (recordedMinutes >= 8 * 60 && hasRecordedToday && !alertedToday) {
                        await userApi.update(refreshedUser.id, { last_alert_eight_hours_recorded_at: moment(new Date()).format('YYYY-MM-DD HH:mm:ss') });
                        enqueueSnackbar('Você registrou 8 horas hoje. +10xp / + 5coins', { variant: 'info' });
                    }
                } catch (eightHourError) {
                    console.error('Falha ao verificar alerta de 8 horas', eightHourError);
                }
            }
            await delay(100);
            if (submitType === 'save') {
                await fetchItem();
                onUndo();
            } else if (submitType === 'saveAndCreateNew') {
                if (projectId && !!permissionByDepartment.perm_menu_projects) {
                    history.replace(`/system/task/project/${projectId}/task/internal/add`);
                } else if (isInternal === true) {
                    history.replace(`/system/task/internal/add`);
                } else {
                    history.replace(`/system/task/add`);
                }
            } else {
                onCancel();
            }
        } catch (e) {
            if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    }

    async function onSubmitTaskPostService(values: TaskPostServiceFormData, attachmentsRemoved: AttachmentTask[], isDirty?: boolean, submitType?: string | null) {
        setLoading(true);
        try {
            const result = await api.updatePostService(id, values.post_service_status, values.software_improvement_needed,
                values.hardware_improvement_needed, values.documentation_improvement_needed);
            if (!result) throw new Error('Erro ao salvar chamado');
            if (attachmentsRemoved && attachmentsRemoved.length > 0) {
                for (const attachmentRemoved of attachmentsRemoved) {
                    await api.deleteAttachment(attachmentRemoved.id);
                }
            }
            if (values.attachments && values.attachments.length > 0) {
                for (const attachment of values.attachments) {
                    if (attachment.id > 0) {
                        if(attachment.edited === true){
                            await api.updateAttachment(attachment.id, attachment.description, attachment.type);
                        }                        
                    } else if (attachment.file || attachment.system_attachment_id) {
                        await api.saveAttachment(attachment.file || null, id, attachment.description, attachment.type, undefined, attachment.system_attachment_id);
                    }
                }
            }
            const relationCommentIdxId: { idx: number, id: number }[] = [];
            if (values.comments && values.comments.length > 0) {
                await Promise.all(values.comments.map(async (comment, index) => {
                    if (comment.id === 0 && comment.comment) {
                        let savedComment = await api.saveComment(id, comment.comment, comment.is_public, comment.user_id, comment.type);
                        relationCommentIdxId.push({ idx: index, id: savedComment.id });
                    }
                }))
            }
            if (relationCommentIdxId.length > 0) {
                await api.sendCommentsEmail(relationCommentIdxId.map(o => o.id));
            }
            enqueueSnackbar('Chamado salvo com sucesso!', { variant: 'success' });
            await delay(100);
            if (submitType === 'save') {
                await fetchItem();
                onUndo();
            } else {
                onCancel();
            }
        } catch (e) {
            if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    }

    async function onSubmitTaskRecurrence(values: TaskRecurrenceFormData, isDirty?: boolean, submitType?: string | null) {
        setLoading(true);
        try {
            const result = await api.updateRecurrence(id, values.recurrence_type, values.recurrence_ticket_status_id,
                (values.recurrence_max && values.recurrence_max > 0 ? values.recurrence_max : null), values.recurrence_value, values.recurrence_message);
            if (!result) throw new Error('Erro ao salvar recorrência');
            const relationCommentIdxId: { idx: number, id: number }[] = [];
            if (values.comments && values.comments.length > 0) {
                await Promise.all(values.comments.map(async (comment, index) => {
                    if (comment.id === 0 && comment.comment) {
                        let savedComment = await api.saveComment(id, comment.comment, comment.is_public, comment.user_id, comment.type);
                        relationCommentIdxId.push({ idx: index, id: savedComment.id });
                    }
                }))
            }
            if (relationCommentIdxId.length > 0) {
                await api.sendCommentsEmail(relationCommentIdxId.map(o => o.id));
            }
            enqueueSnackbar('Recorrência salva com sucesso!', { variant: 'success' });
            await delay(100);
            if (submitType === 'save') {
                await fetchItem();
                onUndo();
            } else {
                onCancel();
            }
        } catch (e) {
            if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    }

    function onCancel() {
        if (history.length > 2) {
            history.goBack();
        } else if (item?.project_id && !!permissionByDepartment.perm_menu_projects) {
            history.push(`/system/task/project/${item.project_id}/task/internal/all`);
        } else {
            history.push(`/system/task/myOpen`);
        }
    }

    function onUndo() {
        fetchItem().then((i) => {
            setEditing(false);
            setTabKey(tabKey + 1);
        });
    }

    if (!item || !commboxId) return null;

    const badgeBaseStyle = { color: 'white', marginLeft: '10px', padding: '0 5px', borderRadius: '4px', display: 'inline-block' };
    const userEdittingTitle = `Tarefa vinculada ao usuário ${item?.user?.name}`;
    const userEdittingStyle = {
        ...badgeBaseStyle,
        background: item?.user_id === user?.id ? '#df342c' : '#6eb91d'
    };

    let emailApprovalBadge: React.ReactNode[] = [];
    if ((item?.requires_email_approval && item?.ticket_type?.approval_email_layout === TicketTypeApprovalEmailLayout.Analysis) || item?.denied_for_analysis_at || item?.approved_for_analysis_at) {
        let approvalBackground = '#f59e0b';
        let approvalLabel = 'Aprovação de análise da demanda PENDENTE';
        if (item?.denied_for_analysis_at) {
            approvalBackground = '#df342c';
            approvalLabel = 'Demanda REPROVADA na análise';
        } else if (item?.approved_for_analysis_at) {
            approvalBackground = '#6eb91d';
            approvalLabel = 'Demanda APROVADA na análise';
        }
        emailApprovalBadge.push(
            <span key={'analysis'} style={{ ...badgeBaseStyle, background: approvalBackground }}>{`${approvalLabel}`}</span>
        );
    }
    if (item?.requires_email_approval || item?.denied_at || item?.approved_at) {
        let approvalBackground = '#f59e0b';
        let approvalLabel = 'Aprovação de execução da demanda PENDENTE';
        if (item?.denied_at) {
            approvalBackground = '#df342c';
            approvalLabel = 'Demanda REPROVADA para execução';
        } else if (item?.approved_at) {
            approvalBackground = '#6eb91d';
            approvalLabel = 'Demanda APROVADA para execução';
        }
        emailApprovalBadge.push(
            <span key={'execution'} style={{ ...badgeBaseStyle, background: approvalBackground }}>{`${approvalLabel}`}</span>
        );
    }

    const title: React.ReactNode = (
        <>
            Edição Chamado - {item?.name}
            <span style={userEdittingStyle}>{userEdittingTitle}</span>
            {emailApprovalBadge}
        </>
    );
    return (
        <>
            <TaskOpenShake
                item={item}
                commboxId={commboxId}
                enable={user?.use_lanza_coin === true}
            />
            <BreadcrumbsItem to={match.url}>
                Edição Chamado - {item?.name}
            </BreadcrumbsItem>
            <Panel panelHeaderTitle={title}>
                <Switch>
                    <Route path={`${match.url}/:tab`}>
                        {({ match }) => (
                            <>
                                <AppBar position="relative" color="default">
                                    <TaskEditTabs
                                        value={match?.params.tab}
                                        onTabChange={onTabChange}
                                        editing={editing}
                                        isCommbox={!!(projectId || commboxId === item.final_customer_id)}
                                        user={user}
                                        item={item}
                                    />
                                </AppBar>
                                {match?.params.tab === 'general' && <TaskForm
                                    title=''
                                    key={`general-${tabKey}`}
                                    item={item}
                                    onCancel={onCancel}
                                    onSubmit={onSubmit}
                                    onChange={setEditing}
                                    onUndo={onUndo}
                                    projectId={projectId}
                                    commboxId={commboxId}
                                />}
                                {match?.params.tab === 'post-service' && <TaskPostServiceForm
                                    title=''
                                    key={`post-service-${tabKey}`}
                                    item={item}
                                    onCancel={onCancel}
                                    onSubmit={onSubmitTaskPostService}
                                    onChange={setEditing}
                                    onUndo={onUndo}
                                />}
                                {match?.params.tab === 'recurrence' && <TaskRecurrenceForm
                                    title=''
                                    key={`recurrence-${tabKey}`}
                                    item={item}
                                    onCancel={onCancel}
                                    onSubmit={onSubmitTaskRecurrence}
                                    onChange={setEditing}
                                    onUndo={onUndo}
                                />}
                            </>
                        )}
                    </Route>
                    <Route>
                        <Redirect to={`${match.url}/general`} />
                    </Route>
                </Switch>
            </Panel>
        </>
    );
}